import React, { useState, useEffect } from 'react';
import './CustomModal.css';
import Swal from 'sweetalert2'; // Importando SweetAlert2
import { getRolesListRoot, getFeaturesList, createRootTenant } from './services'; // Importando el nuevo servicio

interface CustomModalProps {
  show: boolean;
  handleClose: () => void;
  title: React.ReactNode;
  tenantDetails?: any;
  onSave: () => void;
}

const CustomModal: React.FC<CustomModalProps> = ({
  show,
  handleClose,
  title,
  tenantDetails,
  onSave,
}) => {
  // Control de pestañas
  const [activeTab, setActiveTab] = useState<'RootUser' | 'Subscription'>('RootUser');

  // Estados para la pestaña 1 (Root User)
  const [firstName, setFirstName] = useState(''); // Nombre del Root User
  const [lastName, setLastName] = useState(''); // Apellido del Root User
  const [email, setEmail] = useState(''); // Email del Root User
  const [roles, setRoles] = useState<any[]>([]); // Almacena la lista de roles obtenida del servicio
  const [selectedRoles, setSelectedRoles] = useState<any[]>([]); // Roles seleccionados por el usuario

  // Estados para la pestaña 2 (Subscription)
  const [tenantName, setTenantName] = useState('');
  const [subscriptionName, setSubscriptionName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [features, setFeatures] = useState<any[]>([]); // Almacena la lista de features obtenida del servicio
  const [selectedFeatures, setSelectedFeatures] = useState<any[]>([]); // Features seleccionadas
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true); // Control para deshabilitar el botón

  // Validar los campos requeridos
  const validateFields = () => {
    const missingFields: string[] = [];

    if (!firstName.trim()) missingFields.push('Root User First Name');
    if (!lastName.trim()) missingFields.push('Root User Last Name');
    if (!email.trim()) missingFields.push('Root User Email');
    if (!tenantName.trim()) missingFields.push('Tenant Name');
    if (!subscriptionName.trim()) missingFields.push('Subscription Name');

    return missingFields;
  };

  useEffect(() => {
    // Obtiene roles y features al mostrar el modal
    if (show) {
      getRolesListRoot()
        .then((res) => {
          setRoles(res.data); // Almacena los roles obtenidos
        })
        .catch((error) => {
          console.error('Error obteniendo roles:', error);
        });

      getFeaturesList()
        .then((res) => {
          setFeatures(res.data); // Almacena las features obtenidas
        })
        .catch((error) => {
          console.error('Error obteniendo features:', error);
        });
    }

    if (show && tenantDetails) {
      // Si estamos editando un tenant existente, cargamos los valores previos
      setFirstName(tenantDetails.firstName || '');
      setLastName(tenantDetails.lastName || '');
      setEmail(tenantDetails.email || '');
      setSelectedRoles(tenantDetails.roles || []);

      setTenantName(tenantDetails.tenantName || '');
      setSubscriptionName(tenantDetails.subscriptionName || '');
      setStartDate(tenantDetails.startDate || '');
      setEndDate(tenantDetails.endDate || '');
      setSelectedFeatures(tenantDetails.features || []);
    } else if (show && !tenantDetails) {
      // Limpiar el formulario al abrir para un nuevo tenant
      setFirstName('');
      setLastName('');
      setEmail('');
      setSelectedRoles([]);

      setTenantName('');
      setSubscriptionName('');
      setStartDate('');
      setEndDate('');
      setSelectedFeatures([]);
    }
  }, [show, tenantDetails]);

  // Manejador de cambios en la selección de roles
  const handleRoleChange = (roleId: number) => {
    setSelectedRoles((prevSelectedRoles) =>
      prevSelectedRoles.includes(roleId)
        ? prevSelectedRoles.filter((id) => id !== roleId) // Deseleccionar si ya está seleccionado
        : [...prevSelectedRoles, roleId] // Seleccionar si no estaba seleccionado
    );
  };

  // Manejador de cambios en la selección de features
  const handleFeatureChange = (featureId: number) => {
    setSelectedFeatures((prevSelectedFeatures) =>
      prevSelectedFeatures.includes(featureId)
        ? prevSelectedFeatures.filter((id) => id !== featureId) // Deseleccionar si ya está seleccionada
        : [...prevSelectedFeatures, featureId] // Seleccionar si no estaba seleccionada
    );
  };

  // Control para deshabilitar el botón de envío basado en campos requeridos
  useEffect(() => {
    const missingFields = validateFields();
    setIsSubmitDisabled(missingFields.length > 0); // Deshabilitar el botón si algún campo requerido está vacío
  }, [firstName, lastName, email, tenantName, subscriptionName]);

  // Guardar el tenant y root user en una sola petición
  const handleSave = () => {
    const missingFields = validateFields();
    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: `Please complete the following fields: ${missingFields.join(', ')}`,
      });
      return;
    }

    const newTenantData = {
      tenantName,
      tenantDomain: '',
      suscriptionName: subscriptionName,
      startDate,
      endDate,
      features: selectedFeatures.map((featureId) => ({
        id: featureId,
        name: features.find((f) => f.id === featureId)?.name,
      })),
      firstName,
      lastName,
      email,
      roles: selectedRoles.map((roleId) => ({
        id: roleId,
        name: roles.find((r) => r.id === roleId)?.name,
      })),
    };

    // Llamada a la API para crear tenant y root user en una sola petición
    createRootTenant(newTenantData)
      .then(() => {
        Swal.fire({
          icon: 'success',
          title: 'Tenant and Root User created successfully',
        });
        onSave(); // Refrescar la lista de tenants
        handleClose(); // Cerrar el modal
      })
      .catch((error) => {
        // Aquí mostramos el mensaje exacto que proviene del backend
        console.log('Datos del error:', error.response?.data);

        // Acceder correctamente al mensaje dentro de la respuesta
        const errorMessage = error.response?.data?.error || 'An error occurred while creating the tenant and root user';
        Swal.fire({
          icon: 'error',
          title: 'Error creating tenant and root user',
          text: errorMessage,  // Mostramos el mensaje exacto del backend
        });
        console.error('Error creating tenant and root user:', error);
      });
  };

  const handleDisabledSubmit = () => {
    const missingFields = validateFields();
    if (missingFields.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Missing Fields',
        text: `Please complete the following fields: ${missingFields.join(', ')}`,
      });
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-connectivity">
        <div className="custom-modal-header">
          <h4>{title}</h4>
          <button onClick={handleClose} className="close-button">&times;</button>
        </div>

        <div className="tab-header">
          <button
            onClick={() => setActiveTab('RootUser')}
            className={`tab-button ${activeTab === 'RootUser' ? 'active' : ''}`}
          >
            Root User
          </button>
          <button
            onClick={() => setActiveTab('Subscription')}
            className={`tab-button ${activeTab === 'Subscription' ? 'active' : ''}`}
          >
            Subscription
          </button>
        </div>

        {activeTab === 'RootUser' && (
          <div className="tab-content">
            {/* Tab 1 - Root User Information */}
            <div className="form-group">
              <label htmlFor="firstName">Root User First Name <span style={{ color: 'red' }}>*</span></label>
              <input
                id="firstName"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="form-control"
                placeholder="Enter first name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Root User Last Name <span style={{ color: 'red' }}>*</span></label>
              <input
                id="lastName"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="form-control"
                placeholder="Enter last name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Root User Email <span style={{ color: 'red' }}>*</span></label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="form-control"
                placeholder="Enter email"
              />
            </div>

            <div className="form-group">
              <label style={{ marginBottom: '10px' }}>Roles</label>
              <div className="d-flex flex-wrap" style={{ gap: '10px 20px' }}>
                {roles.map((role) => (
                  <div className="form-check" key={role.id} style={{ marginBottom: '10px' }}>
                    <input
                      id={`role${role.id}`}
                      type="checkbox"
                      checked={selectedRoles.includes(role.id)}
                      onChange={() => handleRoleChange(role.id)}
                      className="form-check-input"
                    />
                    <label htmlFor={`role${role.id}`} className="form-check-label">
                      {role.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                onClick={() => setActiveTab('Subscription')}
                className="btn-light-marino f-parrafo"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {activeTab === 'Subscription' && (
          <div className="tab-content">
            {/* Tab 2 - Subscription Information */}
            <div className="form-group">
              <label htmlFor="tenantName">Tenant Name <span style={{ color: 'red' }}>*</span></label>
              <input
                id="tenantName"
                type="text"
                value={tenantName}
                onChange={(e) => setTenantName(e.target.value)}
                className="form-control"
                placeholder="Enter tenant name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="subscriptionName">Subscription Name <span style={{ color: 'red' }}>*</span></label>
              <input
                id="subscriptionName"
                type="text"
                value={subscriptionName}
                onChange={(e) => setSubscriptionName(e.target.value)}
                className="form-control"
                placeholder="Enter subscription name"
              />
            </div>

            <div className="d-flex justify-content-between">
              <div className="form-group" style={{ width: '48%' }}>
                <label htmlFor="startDate">Start Date</label>
                <input
                  id="startDate"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="form-control"
                />
              </div>

              <div className="form-group" style={{ width: '48%' }}>
                <label htmlFor="endDate">End Date</label>
                <input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="form-control"
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ marginBottom: '10px' }}>Features</label>
              <div className="d-flex flex-wrap" style={{ gap: '10px 20px' }}>
                {features.map((feature) => (
                  <div className="form-check" key={feature.id} style={{ marginBottom: '10px' }}>
                    <input
                      id={`feature${feature.id}`}
                      type="checkbox"
                      checked={selectedFeatures.includes(feature.id)}
                      onChange={() => handleFeatureChange(feature.id)}
                      className="form-check-input"
                    />
                    <label htmlFor={`feature${feature.id}`} className="form-check-label">
                      {feature.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            <div className="d-flex justify-content-end mt-3">
              <button
                onClick={isSubmitDisabled ? handleDisabledSubmit : handleSave}
                className={`btn-light-marino f-parrafo ${isSubmitDisabled ? 'disabled' : ''}`}
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomModal;
